import React, { useEffect, useRef, useState } from 'react'
import { Select } from '../../../ui'
import { useStore } from '../../../StateProvider'
import { equals, isNil, map } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { fetchHedgeTrading } from '../../api'
import { useParamsObj, usePresetValue } from '../../utils/hooks'
import { getUserValue, useGetPlatform, useGetUserOrFormValue } from '../utils'
import styles from './styles.css'
import queryString from 'query-string'

const ExternalSelectHedgeTrading = ({ label, name, additionalInfo, showLabelAsPlaceholder, dependOnFldValues }) => {
  const { t, i18n } = useTranslation()
  const [available, setAvailable] = useState(false)
  const [value, setValue] = useState('')
  const valueRef = useRef('')
  const [fetchError, setFetchError] = useState('')
  const [hedgeTradingOptions, setHedgeTradingOptions] = useState([])
  const [isValid, setIsValid] = useState(null)
  const [{
    user,
    user: { brokerGroup, utmCampaign, deptCode, fundId },
    form,
    form: { accId }
  }, dispatch] = useStore()
  const location = useLocation()
  const userValue = getUserValue(accId, user, name)
  const ctierId = useGetUserOrFormValue('ctierId')
  const depositCurrency = useGetUserOrFormValue('depositCurrency')
  const platform = useGetPlatform()

  const paramsObj = useParamsObj(
    { brokerGroup, utmCampaign, deptCode, fundId, search: location.search })
  const { fund_id: queryFundId = '' } = queryString.parse(location.search)
  const visibleRef = useRef(true)

  const presetValue = (value) => {
    if (typeof value === 'boolean') {
      value = String(value)
    }
    setValue(value)
    valueRef.current = value
    const isValid = value.length > 0
    setIsValid(isValid)
    dispatch({ type: 'setExtFieldValue', name, value: value === 'true' })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  useEffect(() => {
    if (queryFundId.length) {
      visibleRef.current = false
      dispatch({ type: 'removeFieldValue', name })
      dispatch({ type: 'removeValidationFld', name })
    } else if (!visibleRef.current) {
      visibleRef.current = true
      dispatch({ type: 'addValidationFld', name })
      presetValue(valueRef.current)
    }
  }, [queryFundId])

  useEffect(() => {
    if (visibleRef.current) {
      if (isNil(dependOnFldValues)) {
        setAvailable(true)
      } else {
        let newAvailable = true
        for (const [field, values] of Object.entries(dependOnFldValues)) {
          if (field === 'platform') {
            if (!values.includes(platform)) {
              newAvailable = false
              break
            }
          } else if (!values.includes(form[accId][field])) {
            newAvailable = false
            break
          }
        }
        setAvailable(newAvailable)
      }
    }
  }, [form, accId, dependOnFldValues, platform])

  useEffect(() => {
    if (available) {
      dispatch({ type: 'addValidationFld', name })
      presetValue(valueRef.current)
    } else {
      dispatch({ type: 'removeValidationFld', name })
      dispatch({ type: 'removeFieldValue', name })
    }
  }, [available, name])

  usePresetValue(name, userValue, presetValue)

  useEffect(() => {
    if (available && visibleRef.current) {
      setFetchError('')
      if (ctierId == null) {
        if (hedgeTradingOptions.length) {
          setHedgeTradingOptions([])
          presetValue('')
        }
      } else {
        const params = {
          ...paramsObj, ctierId, depositCurrency
        }

        fetchHedgeTrading(params).then(/**
         * @param {Object.<string, boolean>} response
         */(response) => {
            const options = Object.entries(response)
              .filter(([_, available]) => available)
              .map(([key]) => ({
                value: (key === 'hedgeTrading').toString(), label: t(`generatedField.hedgeTradingOption.${key}`)
              }))
            if (!options.length) {
              setFetchError('Can\'t get available trading mode')
            } else {
              setFetchError('')
            }
            setHedgeTradingOptions(options)

            if (options.length === 1) {
              valueRef.current = options[0].value
              dispatch({ type: 'setExtFieldValue', name, value: options[0].value === 'true' })
              dispatch({ type: 'setValidationFld', name, value: true })
            } else if (valueRef.current && !options.find((option) => option.value === valueRef.current)) {
              presetValue('')
            }
          }, (error) => {
            setFetchError(error.message)
            presetValue('')
          })
      }
    }
  }, [i18n.language, ctierId, depositCurrency, available])

  /**
   * @param {string} value
   */
  const getSelectValue = (value) => (equals(value, t('generatedField.choose')) ? '' : value)

  const onChange = ({ target: { value, name } }) => {
    const selectedValue = getSelectValue(value)
    setValue(selectedValue)
    valueRef.current = selectedValue
    setIsValid(!!selectedValue.length)

    dispatch({ type: 'setExtFieldValue', name, value: selectedValue === 'true' })
    dispatch({ type: 'setValidationFld', name, value: !!selectedValue.length })
  }

  if (!available || !visibleRef.current) {
    return null
  }

  return (
    <>
      {hedgeTradingOptions.length > 1 ? (
        <Select
          value={value || ''}
          label={t(label)}
          placeholder={t('generatedField.choose')}
          options={hedgeTradingOptions}
          onChange={onChange}
          name={name}
          key={name}
          error=''
          valid={isValid}
          info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
          showLabelAsPlaceholder={showLabelAsPlaceholder}
        />
      ) : null}
      {fetchError.length > 0 && <span className={styles.error}>{fetchError}</span>}
    </>
  )
}

const getExternalHedgeTradingSelectFields = map(platformField => {
  const { i18n, name, required, order, dependOnFldValues } = platformField
  return (
    <ExternalSelectHedgeTrading
      label={i18n ? `generatedField.${i18n}` : ''}
      name={name}
      required={required}
      key={name}
      order={order}
      dependOnFldValues={dependOnFldValues}
    />
  )
})

export { ExternalSelectHedgeTrading, getExternalHedgeTradingSelectFields }
